import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import ArchiveRow from "@/components/ArchiveRow";
import { computeStats } from "@/data/archives";
import { useLang } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = 10;

const Archive = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [siteStats, setSiteStats] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/archive?limit=500`);
        if (!ignore) setRows(data);
      } catch (e) {
        console.error("archive fetch failed", e);
      }
      try {
        const { data } = await axios.get(`${API}/settings/stats`);
        if (!ignore) setSiteStats(data);
      } catch (e) {
        console.error("site stats fetch failed", e);
      }
      try {
        const { data } = await axios.get(`${API}/settings/archive-notice`);
        if (!ignore) setNotice(data);
      } catch (e) {
        console.error("archive notice fetch failed", e);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const visible = expanded ? rows : rows.slice(0, INITIAL);
  const canExpand = rows.length > INITIAL && !expanded;

  const computed = computeStats(rows);
  const s = siteStats || {
    total_tips: computed.total,
    wins: computed.wins,
    losses: computed.losses,
    win_rate: computed.winRate,
  };

  const goToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="archive"
      data-testid="archive-section"
      className="py-20 md:py-32 border-t border-white/10"
    >
      <div className="container-hk">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="text-[10px] md:text-xs tracking-[0.35em] text-[var(--hk-red)] font-bold mb-3">
              // 004 · {t.archive.overline}
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.95] tracking-tight">
              {t.archive.title}
            </h2>
          </div>
          <p className="text-sm text-white/60 max-w-sm">{t.archive.subtitle}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10 md:mb-14">
          <HomeStat label={t.stats.total_tips} value={s.total_tips} testid="home-stat-total" />
          <HomeStat label={t.archive.win + "S"} value={s.wins} testid="home-stat-wins" />
          <HomeStat label={t.archive.loss + "ES"} value={s.losses} testid="home-stat-losses" />
          <HomeStat label="WIN RATE" value={`${s.win_rate}%`} testid="home-stat-winrate" />
        </div>

        {notice?.enabled && notice.text && (
          <p
            data-testid="home-archive-notice"
            className="-mt-4 md:-mt-6 mb-8 text-center text-sm md:text-base font-semibold text-[var(--hk-red)] leading-relaxed max-w-3xl mx-auto px-2"
          >
            {notice.text}
          </p>
        )}

        <div className="border border-white/10">
          {visible.map((row) => (
            <ArchiveRow key={row.id || row.iso} row={row} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-10 flex flex-col sm:flex-row items-stretch justify-center gap-3 sm:gap-4"
        >
          <button
            onClick={() => setExpanded(true)}
            disabled={!canExpand}
            data-testid="view-more-btn"
            className={`btn-outline px-8 py-4 text-[11px] font-bold tracking-[0.28em] ${!canExpand ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            {expanded ? "ALL ARCHIVE SHOWN" : "VIEW MORE ↓"}
          </button>
          <button
            onClick={goToContact}
            data-testid="subscribe-full-btn"
            className="btn-red px-8 py-4 text-[11px] font-bold tracking-[0.28em]"
          >
            SUBSCRIBE AND SEE FULL ARCHIVE — OVER 3K TIPS →
          </button>
        </motion.div>
      </div>
    </section>
  );
};

const HomeStat = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-black p-6 md:p-8 relative overflow-hidden group">
    <div className="text-[10px] tracking-[0.3em] text-white/50 mb-3">{label}</div>
    <div className="font-display text-4xl md:text-5xl tracking-tight">{value}</div>
    <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[var(--hk-red)] group-hover:w-full transition-[width] duration-500" />
  </div>
);

export default Archive;
